import { useState } from "react";
import { AuthorApi, authorsApi, useAuthors } from "api/shop";
import { useToaster } from "utils";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { PaziaModal, UserAvatar } from "components/molecules";
import { PaziaButton, PaziaLoader } from "components/atoms";
import { MdEdit, MdDelete } from "react-icons/md";
import { AuthorFormModal } from "./AuthorFormModal";

interface AuthorsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AuthorsModal = ({ isOpen, onClose }: AuthorsModalProps) => {
  const { authors, isLoading, invalidate } = useAuthors();
  const { showSuccess, showError } = useToaster();

  const [isCreating, setIsCreating] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState<AuthorApi | null>(null);

  const handleDelete = async (author: AuthorApi) => {
    if (!confirm(`Czy na pewno chcesz usunąć autora "${author.firstName} ${author.lastName}"?`)) return;
    try {
      await authorsApi.deleteAuthor(author.id);
      showSuccess("Usunięto autora");
      invalidate();
    } catch (e) {
      showError("Nie udało się usunąć autora");
      console.error(e);
    }
  };

  return (
    <PaziaModal id="authors-modal" onCloseRequest={onClose} isOpen={isOpen} size="2xl">
      {isOpen && (
        <ModalPadding>
          <ModalHeader>Autorzy</ModalHeader>

          {isLoading ? (
            <PaziaLoader position="center" />
          ) : (
            <div className="space-y-2">
              {authors.length === 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400">Brak autorów.</p>
              )}
              {authors.map((author) => (
                <div
                  key={author.id}
                  className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-600 dark:bg-slate-800"
                >
                  <UserAvatar avatar={author.avatar} firstName={author.firstName} surname={author.lastName} />
                  <div className="min-w-0 flex-1 font-medium text-slate-800 dark:text-slate-100">
                    {author.firstName} {author.lastName}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditingAuthor(author)}
                      className="rounded p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-indigo-600 dark:hover:bg-slate-600 dark:hover:text-indigo-400"
                      title="Edytuj"
                    >
                      <MdEdit size={20} />
                    </button>
                    <button
                      onClick={() => handleDelete(author)}
                      className="rounded p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-red-600 dark:hover:bg-slate-600 dark:hover:text-red-400"
                      title="Usuń"
                    >
                      <MdDelete size={20} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 flex justify-end border-t border-slate-200 pt-4 dark:border-slate-600">
            <PaziaButton type="primary" title="Dodaj autora +" onClick={() => setIsCreating(true)} />
          </div>
        </ModalPadding>
      )}

      <AuthorFormModal
        isOpen={isCreating}
        onCloseRequest={() => setIsCreating(false)}
        onSuccess={() => {
          invalidate();
          setIsCreating(false);
        }}
      />
      <AuthorFormModal
        isOpen={editingAuthor !== null}
        onCloseRequest={() => setEditingAuthor(null)}
        author={editingAuthor ?? undefined}
        onSuccess={() => {
          invalidate();
          setEditingAuthor(null);
        }}
      />
    </PaziaModal>
  );
};
